import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import Logo from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-1" role="radiogroup" aria-label="Rating">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={`star-${n}`}
        onClick={() => onChange(n)}
        className="p-1 focus:outline-none focus:ring-2 focus:ring-[#C86D51] rounded"
        aria-label={`${n} stars`}
      >
        <Star
          className={`w-8 h-8 transition-colors ${
            n <= value ? "fill-[#C86D51] text-[#C86D51]" : "text-[#0D253A]/25 hover:text-[#C86D51]"
          }`}
        />
      </button>
    ))}
  </div>
);

const LeaveReview = () => {
  const { token } = useParams();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [alreadyDone, setAlreadyDone] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [city, setCity] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/reviews/token/${token}`)
      .then(({ data }) => {
        if (!alive) return;
        setRecord(data);
        setAlreadyDone(data.status !== "invited");
        if (data.guest_city) setCity(data.guest_city);
      })
      .catch(() => alive && setInvalid(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token]);

  const submit = async () => {
    if (text.trim().length < 10) {
      toast.error(t.reviews.submit.error);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/reviews/token/${token}`, {
        rating,
        title,
        text,
        guest_city: city,
      });
      setSubmitted(true);
      toast.success(t.reviews.submit.success);
    } catch (e) {
      toast.error(e.response?.data?.detail || t.reviews.submit.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-xl bg-white border border-[#0D253A]/15 text-sm focus:border-[#C86D51] focus:outline-none transition-colors";

  return (
    <main
      data-testid="leave-review-page"
      className="min-h-screen pt-28 pb-24 px-6 sm:px-12 flex items-start justify-center bg-[#F8F5F0]"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-xl bg-white rounded-3xl border border-[#0D253A]/10 p-8 sm:p-10 shadow-[0_30px_70px_-40px_rgba(13,37,58,0.35)]"
      >
        <div className="mb-6">
          <Logo />
        </div>

        {loading && <p className="text-sm text-[#5C6773]">…</p>}

        {!loading && invalid && (
          <p data-testid="review-invalid" className="text-base text-[#0D253A]">
            {t.reviews.submit.invalid}
          </p>
        )}

        {!loading && !invalid && (submitted || alreadyDone) && (
          <div data-testid="review-thankyou" className="text-center py-4">
            <CheckCircle2 className="w-12 h-12 text-[#C86D51] mx-auto mb-4" />
            <h1 className="font-display text-2xl text-[#0D253A]">
              {t.reviews.submit.success}
            </h1>
            {alreadyDone && !submitted && (
              <p className="mt-3 text-sm text-[#5C6773]">{t.reviews.submit.alreadyDone}</p>
            )}
          </div>
        )}

        {!loading && !invalid && !submitted && !alreadyDone && record && (
          <>
            <h1 className="font-display text-3xl text-[#0D253A]">{t.reviews.submit.title}</h1>
            <p className="mt-2 text-sm text-[#5C6773]">
              {t.reviews.submit.intro}
              {record.stay_period && (
                <span className="block mt-1 font-mono-alt text-xs">{record.stay_period}</span>
              )}
            </p>

            <div className="mt-8 space-y-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">
                  {t.reviews.submit.rating}
                </label>
                <StarPicker value={rating} onChange={setRating} />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">
                  {t.reviews.submit.titleField}
                </label>
                <input
                  data-testid="review-title-input"
                  className={inputCls}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={140}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">
                  {t.reviews.submit.city}
                </label>
                <input
                  data-testid="review-city-input"
                  className={inputCls}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  maxLength={100}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C6773] mb-2">
                  {t.reviews.submit.text}
                </label>
                <textarea
                  data-testid="review-text-input"
                  className={`${inputCls} resize-none`}
                  rows={6}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={2000}
                  placeholder={t.reviews.submit.textPh}
                />
              </div>
              <button
                data-testid="review-submit-button"
                onClick={submit}
                disabled={sending}
                className="w-full px-6 py-3.5 bg-[#C86D51] text-white text-sm font-semibold rounded-full hover:bg-[#0D253A] disabled:opacity-60 transition-colors"
              >
                {sending ? t.reviews.submit.sending : t.reviews.submit.cta}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </main>
  );
};

export default LeaveReview;
